import { useState } from 'react';
import Breadcrumb from '@/components/base/Breadcrumb';
import { siteInfo, reputationData } from '@/mocks/comics';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const aboutJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于汗汗漫画 - 免费韩国漫画在线阅读平台',
    description: siteInfo.description,
    url: 'https://hanhancomic.com/about',
    isPartOf: {
      '@type': 'WebSite',
      name: '汗汗漫画',
      url: 'https://hanhancomic.com',
    },
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('loading');
    setFormError('');

    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('website_alt') as string;
    if (honeypot && honeypot.trim() !== '') {
      setFormStatus('success');
      form.reset();
      return;
    }

    formData.delete('website_alt');

    try {
      const res = await fetch(
        'https://readdy.ai/api/form/d9ot0oqqs38sj6jc7f10',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          body: new URLSearchParams(
            Array.from(formData.entries()).map(([k, v]) => [k, v.toString()])
          ).toString(),
        }
      );

      const responseText = await res.text();
      let parsed: Record<string, unknown> | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (res.ok && parsed && (parsed as Record<string, unknown>).code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg =
          (parsed as Record<string, unknown> | null)?.meta &&
          typeof (parsed as Record<string, unknown>).meta === 'object'
            ? String(((parsed as Record<string, unknown>).meta as Record<string, string>).message || '')
            : '';
        if (serverMsg.toLowerCase().includes('spam')) {
          setFormStatus('success');
          form.reset();
        } else {
          setFormError(serverMsg || '提交失败，请稍后重试');
          setFormStatus('error');
        }
      }
    } catch {
      setFormError('网络错误，请检查连接后重试');
      setFormStatus('error');
    }
  };

  const breadcrumbItems = [{ label: '首页', href: '/' }, { label: '关于我们' }];

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(aboutJsonLd) }}
      />

      <main>
        {/* Page Header */}
        <section className="bg-background-100 py-10 md:py-14">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="mb-4">
              <Breadcrumb items={breadcrumbItems} />
            </div>
            <h1 className="text-2xl md:text-3xl font-bold text-foreground-950">
              关于汗汗漫画
            </h1>
            <p className="text-sm text-foreground-500 mt-2">
              了解我们的品牌故事与使命
            </p>
          </div>
        </section>

        {/* Brand Introduction */}
        <section className="py-14 md:py-20 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex flex-col lg:flex-row items-center gap-10 lg:gap-16">
              <div className="lg:w-1/2">
                <img
                  src="https://readdy.ai/api/search-image?query=Korean%20illustration%20style%2C%20cozy%20reading%20room%20with%20bookshelves%20and%20warm%20lighting%2C%20comfortable%20armchair%20with%20reading%20lamp%2C%20warm%20amber%20and%20cream%20tones%2C%20intellectual%20atmosphere%2C%20soft%20artistic%20texture%2C%20inviting%20and%20peaceful%20mood&width=800&height=600&seq=about-brand-2026&orientation=landscape"
                  alt="汗汗漫画品牌形象"
                  className="w-full rounded-xl"
                  title="汗汗漫画 - 品牌形象"
                />
              </div>
              <div className="lg:w-1/2">
                <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
                  我们的品牌故事
                </h2>
                <div className="space-y-4 text-sm text-foreground-600 leading-relaxed">
                  <p>
                    汗汗漫画成立于2020年，是国内领先的韩国漫画在线阅读平台。我们致力于为中国读者提供优质、免费的韩国漫画阅读体验，让每一位漫画爱好者都能轻松畅享正版韩漫资源。
                  </p>
                  <p>
                    平台汇集了来自韩国各大知名漫画平台和出版社的正版授权作品，涵盖<strong>恋爱、奇幻、动作、悬疑、搞笑、剧情、日常、恐怖、耽美</strong>等丰富题材，满足不同读者的阅读偏好。
                  </p>
                  <p>
                    我们始终秉持"免费阅读，品质优先"的理念，与韩国多家顶级漫画内容提供商建立了长期稳定的合作关系，确保平台内容的丰富性和更新的及时性。目前，汗汗漫画已拥有超过<strong>{siteInfo.stats.totalComics}部漫画资源</strong>，累计服务<strong>{siteInfo.stats.totalReaders}读者</strong>。
                  </p>
                  <p>
                    未来，汗汗漫画将继续拓展漫画资源库，优化阅读体验，为国内韩漫爱好者打造更完善的在线阅读社区。无论你是韩漫老读者还是初次接触韩漫的新朋友，汗汗漫画都欢迎你的到来！
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="py-14 md:py-20 bg-background-100">
          <div className="max-w-5xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
              <div className="text-center p-6 rounded-xl bg-background-50">
                <div className="text-3xl md:text-4xl font-bold text-primary-500">
                  {siteInfo.stats.totalComics}
                </div>
                <p className="text-sm text-foreground-500 mt-2">漫画资源</p>
              </div>
              <div className="text-center p-6 rounded-xl bg-background-50">
                <div className="text-3xl md:text-4xl font-bold text-accent-500">
                  {siteInfo.stats.totalReaders}
                </div>
                <p className="text-sm text-foreground-500 mt-2">注册读者</p>
              </div>
              <div className="text-center p-6 rounded-xl bg-background-50">
                <div className="text-3xl md:text-4xl font-bold text-secondary-500">
                  {siteInfo.stats.dailyActive}
                </div>
                <p className="text-sm text-foreground-500 mt-2">日活跃用户</p>
              </div>
              <div className="text-center p-6 rounded-xl bg-background-50">
                <div className="text-3xl md:text-4xl font-bold text-primary-500">
                  {siteInfo.stats.rating}
                </div>
                <p className="text-sm text-foreground-500 mt-2">用户评分</p>
              </div>
            </div>
          </div>
        </section>

        {/* Reputation / Reviews */}
        <section className="py-14 md:py-20 bg-background-50">
          <div className="max-w-5xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950">
                读者口碑
              </h2>
              <p className="text-sm text-foreground-500 mt-2">
                听听读者们怎么说
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {reputationData.map((review) => (
                <div
                  key={review.id}
                  className="p-6 rounded-xl bg-background-100 flex flex-col"
                >
                  <div className="flex items-center gap-0.5 mb-3">
                    {Array.from({ length: 5 }, (_, i) => (
                      <i
                        key={i}
                        className={`text-sm ${
                          i < review.rating
                            ? 'ri-star-fill text-secondary-500'
                            : 'ri-star-line text-foreground-300'
                        }`}
                      ></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed flex-1">
                    &ldquo;{review.content}&rdquo;
                  </p>
                  <div className="flex items-center gap-3 mt-4 pt-4 border-t border-background-200">
                    <img
                      src={review.avatar}
                      alt={review.name}
                      className="w-10 h-10 rounded-full object-cover"
                    />
                    <div>
                      <span className="text-sm font-medium text-foreground-800 block">
                        {review.name}
                      </span>
                      <span className="text-xs text-foreground-400">
                        {review.date}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <section className="py-14 md:py-20 bg-background-100" id="contact">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950">
                联系我们
              </h2>
              <p className="text-sm text-foreground-500 mt-2">
                有任何问题或建议？欢迎与我们联系
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
              <div className="flex flex-col items-center p-5 rounded-xl bg-background-50">
                <div className="w-12 h-12 rounded-full bg-primary-100 flex items-center justify-center mb-3">
                  <i className="ri-mail-line text-xl text-primary-500"></i>
                </div>
                <span className="text-xs text-foreground-500 mb-1">邮箱</span>
                <span className="text-sm font-medium text-foreground-800">{siteInfo.email}</span>
              </div>
              <div className="flex flex-col items-center p-5 rounded-xl bg-background-50">
                <div className="w-12 h-12 rounded-full bg-accent-100 flex items-center justify-center mb-3">
                  <i className="ri-phone-line text-xl text-accent-500"></i>
                </div>
                <span className="text-xs text-foreground-500 mb-1">电话</span>
                <span className="text-sm font-medium text-foreground-800">{siteInfo.phone}</span>
              </div>
              <div className="flex flex-col items-center p-5 rounded-xl bg-background-50">
                <div className="w-12 h-12 rounded-full bg-secondary-100 flex items-center justify-center mb-3">
                  <i className="ri-map-pin-line text-xl text-secondary-500"></i>
                </div>
                <span className="text-xs text-foreground-500 mb-1">地址</span>
                <span className="text-sm font-medium text-foreground-800">{siteInfo.address}</span>
              </div>
            </div>

            {/* Contact Form */}
            <form
              onSubmit={handleSubmit}
              data-readdy-form
              className="bg-background-50 rounded-xl p-6 md:p-8 space-y-5"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    姓名 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-300 transition-all"
                    placeholder="请输入您的姓名"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    邮箱 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-300 transition-all"
                    placeholder="请输入您的邮箱"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  主题 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="subject"
                  name="subject"
                  required
                  className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-300 transition-all"
                  placeholder="请输入消息主题"
                />
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  留言内容 <span className="text-red-500">*</span>
                </label>
                <textarea
                  id="message"
                  name="message"
                  required
                  maxLength={500}
                  rows={5}
                  className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-300 transition-all resize-none"
                  placeholder="请输入您的留言内容（最多500字）"
                ></textarea>
                <p className="text-xs text-foreground-400 mt-1">最多500个字符</p>
              </div>

              {/* Honeypot */}
              <div className="website-alt-wrapper">
                <input
                  type="text"
                  name="website_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />
              </div>

              <button
                type="submit"
                disabled={formStatus === 'loading'}
                className="w-full py-3 bg-primary-500 hover:bg-primary-600 text-background-50 font-semibold rounded-lg transition-colors disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap text-sm"
              >
                {formStatus === 'loading' ? (
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-loader-4-line animate-spin"></i>
                    提交中...
                  </span>
                ) : (
                  '发送消息'
                )}
              </button>

              {formStatus === 'success' && (
                <div className="p-4 rounded-lg bg-green-50 text-green-700 text-sm text-center">
                  <i className="ri-checkbox-circle-line mr-1"></i>
                  消息已成功发送！我们会尽快回复您。
                </div>
              )}

              {formStatus === 'error' && (
                <div className="p-4 rounded-lg bg-red-50 text-red-700 text-sm text-center">
                  <i className="ri-error-warning-line mr-1"></i>
                  {formError}
                </div>
              )}
            </form>
          </div>
        </section>
      </main>
    </>
  );
}